/**
 * Helpers for resolving AIS report timestamps on a target.
 *
 * Kept here (colocated with the target card) so both the minimized card
 * favorite fields and the expanded details view resolve the same dates in the
 * same way — including the derived "last AIS message" (newest of terrestrial
 * and satellite).
 */

type AisReportDate = Date | string | null | undefined;

interface AisReportInfo {
  lastAisReport?: AisReportDate;
  lastAisSatReport?: AisReportDate;
  [key: string]: unknown;
}

const toDate = (value: AisReportDate): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Newest of the terrestrial and satellite AIS reports ("last AIS message"). */
export const getNewestAisReport = (info: AisReportInfo): Date | null => {
  const terrestrial = toDate(info?.lastAisReport);
  const satellite = toDate(info?.lastAisSatReport);

  if (!terrestrial) return satellite;
  if (!satellite) return terrestrial;
  return terrestrial.getTime() >= satellite.getTime() ? terrestrial : satellite;
};

/**
 * Field names for the AIS datetime favorite fields. `lastAisMessage` is
 * derived (newest of terrestrial/satellite); the others map to a real property.
 */
export const AIS_DATE_FIELDS = [
  'lastAisReport',
  'lastAisSatReport',
  'lastAisMessage',
] as const;

/**
 * "Time since" favorite fields mapped to the AIS date field they measure.
 */
export const AIS_SINCE_FIELD_SOURCE: Record<string, string> = {
  lastAisReportSince: 'lastAisReport',
  lastAisSatReportSince: 'lastAisSatReport',
  lastAisMessageSince: 'lastAisMessage',
};

/** Resolve an AIS report date for a field name, handling the derived field. */
export const getAisReportDate = (
  info: AisReportInfo,
  fieldName: string,
): Date | null => {
  if (fieldName === 'lastAisMessage') return getNewestAisReport(info);
  return toDate(info?.[fieldName] as AisReportDate);
};
